import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { UIToggle, UIToggleWithLabel } from "@View/components/common/UIToggle";
import React, { useEffect, useMemo, useState } from "react";
import { CHARTS_COLOR_PALETTE } from "@View/constants";
import { removeKeyFromArrayOfObjects } from "@Utils/charts";

const wrapper = {
  background: "#FFFFFF",
  borderRadius: "10px",
  position: "relative",
  padding: "0 20px 20px 0",
};

const CustomizedLegend = (props) => {
  const { t } = useTranslation();
  const { payload, dataType } = props;

  const incomePayload = payload.filter((e) => e.value.indexOf("income") > 0);
  const outgoingPayload = payload.filter(
    (e) => e.value.indexOf("outgoing") > 0
  );

  const descriptionItemsFontSize = dataType === "counterpartiesTable" ? 13 : 15;

  return (
    <div
      style={{
        display: "flex",
        flexFlow: "row",
        justifyContent: "space-evenly",
        gap: "15px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "15px",
        }}
      >
        <div
          style={{
            color: "#606a74",
            fontWeight: 600,
            fontSize: 18,
            whiteSpace: "nowrap",
          }}
        >
          {t("Charts.TwoBarChart.incomePayments")}
          <UIToggleWithLabel
            sx={{ fontSize: "14px" }}
            control={
              <UIToggle
                checked={props.isIncomeOpen}
                onChange={(event) => {
                  props.setIsIncomeOpen(event.target.checked);
                }}
              />
            }
            label={""}
            labelPlacement="start"
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "5px 15px",
          }}
        >
          {incomePayload.map((entry, index) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <div
                style={{
                  minWidth: "8px",
                  minHeight: "8px",
                  borderRadius: "50%",
                  background: entry.color,
                }}
              ></div>
              <div
                style={{
                  color: entry.color,
                  fontSize: descriptionItemsFontSize,
                }}
                key={`item-${index}`}
              >
                {entry.value.split("-")[0]}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "15px",
        }}
      >
        <div
          style={{
            color: "#606a74",
            fontWeight: 600,
            fontSize: 18,
            whiteSpace: "nowrap",
          }}
        >
          {t("Charts.TwoBarChart.outgoingPayments")}
          <UIToggleWithLabel
            sx={{ fontSize: "14px" }}
            control={
              <UIToggle
                checked={props.isOutcomeOpen}
                onChange={(event) => {
                  props.setIsOutcomeOpen(event.target.checked);
                }}
              />
            }
            label={""}
            labelPlacement="start"
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
          }}
        >
          {outgoingPayload.map((entry, index) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <div
                style={{
                  minWidth: "8px",
                  minHeight: "8px",
                  borderRadius: "50%",
                  background: entry.color,
                }}
              ></div>
              <div
                style={{
                  color: entry.color,
                  fontSize: descriptionItemsFontSize,
                }}
                key={`item-${index}`}
              >
                {entry.value.split("-")[0]}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();

  const numberFormst = new Intl.NumberFormat(i18n.language, {
    style: "decimal",
    maximumFractionDigits: 0,
  });

  if (active && payload && payload.length) {
    const keys = Object.keys(payload[0].payload);
    const incomeKeys = keys.filter((e) => e.indexOf("income") > 0);
    const outgoingKeys = keys.filter((e) => e.indexOf("outgoing") > 0);

    const incomeSum = incomeKeys.reduce(
      (s, i) => s + Number(payload[0].payload[i]),
      0
    );
    const outgoingSum = outgoingKeys.reduce(
      (s, i) => s + Number(payload[0].payload[i]),
      0
    );

    return (
      <div className="custom-tooltip">
        {incomeKeys.length > 0 ? (
          <span>{t("Charts.TwoBarChart.Income")}</span>
        ) : null}
        {incomeKeys.map((e) => {
          let str = e.split("-");
          return (
            <p>
              {str[0]} : {numberFormst.format(payload[0].payload[e])} (
              {Math.round((Number(payload[0].payload[e]) / incomeSum) * 100)}%)
            </p>
          );
        })}
        {outgoingKeys.length > 0 ? (
          <span style={{ marginTop: "15px" }}>
            {t("Charts.TwoBarChart.Outgoing")}
          </span>
        ) : null}
        {outgoingKeys.map((e) => {
          let str = e.split("-");
          return (
            <p>
              {str[0]} : {numberFormst.format(payload[0].payload[e])} (
              {Math.round((Number(payload[0].payload[e]) / outgoingSum) * 100)}
              %)
            </p>
          );
        })}
        <p
          style={{ textAlign: "center", marginTop: "5px", color: "#616161" }}
          className="label"
        >{`${label}`}</p>
      </div>
    );
  }

  return null;
};

const TwoBarChart = ({
  data2,
  // colors,
  Tittle,
  wrapperWidth = "300px",
  dataType,
}) => {
  const [isIncomeOpen, setIsIncomeOpen] = useState(true);
  const [isOutcomeOpen, setIsOutcomeOpen] = useState(true);
  const { i18n } = useTranslation();
  const [colorsMap, setColorsMap] = useState(new Map());

  useEffect(() => {
    const data = JSON.parse(JSON.stringify(data2));
    const keys = Object.keys(data[0]).filter((f) => f !== "date");
    const newColorMap = new Map();

    keys.forEach((item, i) => {
      newColorMap.set(item, CHARTS_COLOR_PALETTE[i]);
    });
    setColorsMap(newColorMap);
  }, [data2]);

  const data = useMemo(() => {
    if (data2.length === 0) return { data: null, keys: null };
    const data = JSON.parse(JSON.stringify(data2));

    if (!isIncomeOpen) {
      removeKeyFromArrayOfObjects(data, "-income");
    }
    if (!isOutcomeOpen) {
      removeKeyFromArrayOfObjects(data, "-outgoing");
    }

    const keys = Object.keys(data[0]).filter((f) => f !== "date");

    return { data, keys };
  }, [data2, isIncomeOpen, isOutcomeOpen]);

  if (data.data === null || data.keys === null) return null;

  return (
    <div style={wrapper}>
      <div
        style={{
          position: "absolute",
          bottom: dataType === "affiliatedEntitiesTable" ? 22 : 42,
          left:
            String(data.data[0][data.keys[0]]).split(".")[0].length > 3
              ? 65
              : 20,
          fontWeight: 500,
          fontSize: "12px",
          lineHeight: "15px",
          letterSpacing: "0.0075em",
          color: "#A1ADBA",
        }}
      >
        {Tittle}
      </div>
      <ResponsiveContainer>
        <BarChart
          width={
            Number.isFinite(wrapperWidth) && wrapperWidth < 585
              ? 585
              : wrapperWidth
          }
          height={580}
          data={data.data}
          margin={{
            top: 100,
            right: 20,
            left: 20,
            bottom: 0,
          }}
        >
          <XAxis dataKey="date" tickLine={false} />
          <YAxis
            axisLine={true}
            tickLine={true}
            width={
              String(data.data[0][data.keys[0]]).split(".")[0].length > 3
                ? 100
                : 55
            }
            tickFormatter={(number) => {
              if (i18n.language === "en")
                return new Intl.NumberFormat().format(number);
              else
                return new Intl.NumberFormat("ru", { style: "decimal" }).format(
                  number
                );
            }}
          />
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <Legend
            verticalAlign="top"
            content={
              <CustomizedLegend
                dataType={dataType}
                isIncomeOpen={isIncomeOpen}
                isOutcomeOpen={isOutcomeOpen}
                setIsIncomeOpen={setIsIncomeOpen}
                setIsOutcomeOpen={setIsOutcomeOpen}
              />
            }
            wrapperStyle={{
              top: 22,
            }}
          />
          <Tooltip
            formatter={(number) => {
              if (i18n.language === "en")
                return new Intl.NumberFormat().format(number);
              else
                return new Intl.NumberFormat("ru", { style: "decimal" }).format(
                  number
                );
            }}
            content={<CustomTooltip />}
          />
          {data.keys.map((item, i) => {
            const stack = item.split("-").pop();
            return (
              <Bar
                barSize={30}
                dataKey={item}
                stackId={stack}
                fill={colorsMap.get(item)}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TwoBarChart;
